import { TextCodes } from '@api/types/translation/TextCodes';
import { BlockCard, Placeholder, TableHeader, TableItemSkeleton } from '@components';
import { Alert, Box, styled } from '@mui/material';
import { breakpoints } from '@themes/theme.helpers';
import { t } from '@translations';

export type Column = {
  key: string;
  name: string;
  sortable?: boolean;
};

interface TableProps<T> {
  renderRow: (item: T) => React.ReactNode;
  list?: T[] | null;
  itemsOnPage?: number;
  isWorking?: boolean;
  headerColumns?: Array<Column>;
  isError?: boolean;
}

export const Table = <T extends unknown>({
  list = [],
  renderRow,
  itemsOnPage,
  isWorking,
  isError,
  headerColumns,
}: TableProps<T>) => {
  return (
    <BlockCard>
      <GridWrapper $columnCount={headerColumns?.length || 1}>
        {headerColumns && <TableHeader headerColumns={headerColumns} />}
        {isWorking && (
          <Placeholder
            numberOfItems={itemsOnPage}
            component={<TableItemSkeleton headerColumns={headerColumns} />}
          />
        )}
        {list?.map((item) => renderRow(item))}
      </GridWrapper>
      {!isWorking && !isError && list?.length === 0 && (
        <Box sx={{ py: 2 }}>
          <Alert severity='info'>{t(TextCodes.Base.NoResults)}</Alert>
        </Box>
      )}
      {!isWorking && isError && (
        <Box sx={{ py: 2 }}>
          <Alert severity='error'>{t(TextCodes.Base.GenericError.UserNotAuthorized)}</Alert>
        </Box>
      )}
    </BlockCard>
  );
};

// GRID WRAPPER
//---------------

type GridWrapperProps = {
  $columnCount?: number;
};

const GridWrapper = styled(Box, {
  shouldForwardProp: (prop) => prop !== '$columnCount',
})<GridWrapperProps>`
  ${breakpoints.up('md')} {
    width: 100%;
    display: grid;
    grid-template-columns: repeat(${(props) => props.$columnCount}, minmax(20px, auto));
    grid-template-rows: auto;
    border-bottom: 1px solid #f3f4f7;
  }
`;
